import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Users, CreditCard, ScanLine, DollarSign, ArrowLeft, Loader2, Trash2,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-border bg-card p-5 hover:border-white/20 transition-colors">
      <div className="flex items-center gap-2 text-[#D4C3A3] mb-3">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className="font-display text-3xl font-extrabold tracking-tight font-mono-num">{value}</p>
      {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [cleaning, setCleaning] = useState(false);

  useEffect(() => {
    api.get("/admin/stats")
      .then((res) => setStats(res.data))
      .catch(() => { setError(true); });
  }, []);

  const cleanupTestAccounts = async () => {
    if (!window.confirm("Delete all diagnostic test accounts (@example-diagnostic.com) and their data?")) return;
    setCleaning(true);
    try {
      const res = await api.delete("/admin/test-accounts");
      toast.success(`Removed ${res.data.deleted} test account(s)`);
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (e) {
      toast.error("Cleanup failed");
    } finally {
      setCleaning(false);
    }
  };

  if (error) {
    return (
      <div className="dark min-h-screen bg-background text-foreground noise-overlay flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p data-testid="admin-forbidden" className="text-lg font-semibold">You don't have access to this page.</p>
        <button onClick={() => navigate("/dashboard")} className="text-[#D4C3A3] text-sm font-semibold">Back to dashboard</button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="dark min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4C3A3]" />
      </div>
    );
  }

  const { users, subscriptions: subs, receipts, recent_signups, funnel } = stats;

  const funnelStages = funnel ? [
    { label: "Visitors", value: funnel.visitors, testid: "funnel-visitors" },
    { label: "Signup started", value: funnel.signup_started, testid: "funnel-signup-started" },
    { label: "Signed up", value: funnel.signups, testid: "funnel-signups" },
    { label: "Checkout started", value: funnel.checkout_started, testid: "funnel-checkout" },
    { label: "Paid", value: funnel.paid, testid: "funnel-paid" },
  ] : [];
  const funnelMax = funnelStages.reduce((m, s) => Math.max(m, s.value || 0), 0) || 1;
  const pctOf = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

  return (
    <div className="dark min-h-screen bg-background text-foreground noise-overlay">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/brand-icon-gold.png" alt="Receipt Republic" className="h-9 w-9 object-contain rounded-lg" />
            <span className="font-display font-extrabold text-lg tracking-tight">Admin · Receipt Republic</span>
          </div>
          <button data-testid="admin-back-button" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10 space-y-8" data-testid="admin-dashboard">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4C3A3] font-bold mb-2">Business overview</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">Stats dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Signed in as {user?.email}</p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-sm">Maintenance</p>
            <p className="text-sm text-muted-foreground">Remove diagnostic test accounts (@example-diagnostic.com) created during debugging.</p>
          </div>
          <Button data-testid="cleanup-test-accounts" onClick={cleanupTestAccounts} disabled={cleaning} variant="outline" className="rounded-xl border-rose-500/30 bg-transparent text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 shrink-0">
            {cleaning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />} Clean up test accounts
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard testid="stat-total-users" icon={Users} label="Total users" value={users.total} sub={`${users.today} today · ${users.this_week} this week`} />
          <StatCard testid="stat-paying" icon={CreditCard} label="Paying subscribers" value={subs.paying} sub={`${subs.trialing} on trial`} />
          <StatCard testid="stat-mrr" icon={DollarSign} label="Est. MRR" value={`$${subs.estimated_mrr}`} sub={`$6/mo × ${subs.paying}`} />
          <StatCard testid="stat-receipts" icon={ScanLine} label="Receipts processed" value={receipts.total} sub={`${receipts.today} today · ${receipts.this_week} this week`} />
        </div>

        {funnel && (
          <div data-testid="conversion-funnel" className="rounded-2xl border border-border bg-card p-6">
            <div className="flex items-baseline justify-between mb-1">
              <h2 className="font-display font-bold text-lg">Conversion funnel</h2>
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Last 30 days</span>
            </div>
            <p className="text-sm text-muted-foreground mb-6">Real usage — visitor → signup → paid. (Visitor/started counts began when tracking went live.)</p>
            <div className="space-y-3">
              {funnelStages.map((s, i) => {
                const prev = i > 0 ? funnelStages[i - 1].value : null;
                return (
                  <div key={s.label} data-testid={s.testid}>
                    <div className="flex items-center justify-between text-sm mb-1.5">
                      <span className="font-medium">{s.label}</span>
                      <span className="flex items-center gap-2">
                        <span className="font-display font-bold font-mono-num">{Number(s.value || 0).toLocaleString()}</span>
                        {prev !== null && (
                          <span className="text-xs text-muted-foreground">{pctOf(s.value || 0, prev)}% of prev</span>
                        )}
                      </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${Math.max(2, ((s.value || 0) / funnelMax) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-5 pt-4 border-t border-border flex flex-wrap gap-x-8 gap-y-1 text-sm">
              <span className="text-muted-foreground">Visitor → signup <b className="text-foreground font-mono-num">{pctOf(funnel.signups, funnel.visitors)}%</b></span>
              <span className="text-muted-foreground">Signup → paid <b className="text-foreground font-mono-num">{pctOf(funnel.paid, funnel.signups)}%</b></span>
              <span className="text-muted-foreground">Visitor → paid <b className="text-foreground font-mono-num">{pctOf(funnel.paid, funnel.visitors)}%</b></span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display font-bold mb-4">Subscriptions</h2>
            <ul className="space-y-3 text-sm">
              {[
                ["Active", subs.active],
                ["Trialing", subs.trialing],
                ["Past due", subs.past_due],
                ["Canceled", subs.canceled],
              ].map(([label, val]) => (
                <li key={label} className="flex justify-between border-b border-border pb-2 last:border-0">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-semibold font-mono-num">{val}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display font-bold mb-4">Signups & receipts</h2>
            <ul className="space-y-3 text-sm">
              {[
                ["New signups this month", users.this_month],
                ["Receipts via photo", receipts.by_source.photo],
                ["Receipts via email", receipts.by_source.email],
              ].map(([label, val]) => (
                <li key={label} className="flex justify-between border-b border-border pb-2 last:border-0">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-semibold font-mono-num">{val}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6">
          <h2 className="font-display font-bold mb-4">Recent signups</h2>
          {recent_signups.length === 0 ? (
            <p className="text-sm text-muted-foreground">No users yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4 font-medium">Email</th>
                    <th className="py-2 pr-4 font-medium">Name</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_signups.map((u, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="py-2 pr-4">{u.email}</td>
                      <td className="py-2 pr-4">{u.name || "—"}</td>
                      <td className="py-2 pr-4">{u.subscription_status || (u.subscribed ? "active" : "free")}</td>
                      <td className="py-2 text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
